import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { RUPEE } from "../../../../constants/globalConstants";
import { getOptimizedUrl } from "../../../../constants/globalFunction";
import { initializeRazorpayCheckout, getRazorpayConfig } from "../../../../utils/razorpay";
import CommonButton from "../../../../components/button/CommonButton";
import CommonInput from "../../../../components/input/CommonInput";
import PhoneInput from "../../../../components/input/PhoneInput";
import CountryModal from "../../../../components/modal/CountryModal";
import ConfirmationModal from "../../../../components/modal/ConfirmationModal";
import { appApiHandler } from "../../../../api/appApiHandler";
import { showErrorToast, showSuccessToast } from "../../../../utils/toast";
import { getErrorMessage, getPrice } from "../../../../constants/globalFunction";
import { string } from "../../../../constants/string";
import { colors } from "../../../../constants/colors";
import OffersModal from "../../../checkout/component/offersModal";
import moment from "moment";
import "./BuyNowModal.css";
import PaymentModal from "../../../checkout/component/paymentModal";

const BuyNowModal = ({ isOpen, onClose, product, selectedColor, selectedSize }) => {
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [quantity, setQuantity] = useState(1);
  const [inputQuantity, setInputQuantity] = useState("1");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [applyingCouponId, setApplyingCouponId] = useState(null);
  const [offerModalVisible, setOfferModalVisible] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [productPrice, setProductPrice] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [cod, setCod] = useState(true);
  const [online, setOnline] = useState(true);
  const [allTaxRates, setAllTaxRates] = useState([]);

  // Address form state
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [name, setName] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [country, setCountry] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [countryModalVisible, setCountryModalVisible] = useState(false);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);

  // Address form errors
  const [nameError, setNameError] = useState("");
  const [phonenoError, setPhonenoError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [pincodeError, setPincodeError] = useState("");
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);

  // Get coupon discount amount
  const getCouponDiscount = (coupon, amount) => {
    if (!coupon || !amount) return 0;

    if (coupon?.discount_type === "flat") {
      const flat = parseFloat(coupon?.discount_amount) || 0;
      return Math.min(flat, amount);
    }

    const percentage = parseFloat(coupon?.discount_percentage) || 0;
    const discount = (amount * percentage) / 100;
    return Math.min(discount, amount);
  };

  // Calculate coupon discount and remaining amount
  const couponDiscount = getCouponDiscount(selectedCoupon, subTotal);
  const remainingAmount = Math.max(0, subTotal + taxAmount - couponDiscount);

  // Get addresses
  const getAddresses = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getAddress(reqData);
    if (response?.status === 200 && response?.data) {
      setAddresses(response?.data);
      // Auto-select first address if available
      if (response?.data?.length > 0) {
        setSelectedAddress(response?.data[0]);
      }
    }
  };

  // Check if coupon is valid today
  const isCouponValidToday = (coupon) => {
    const expiryDate = coupon?.expiry_date;
    const today = moment().startOf("day");

    if (!expiryDate) {
      return true;
    }

    const expiry = moment(expiryDate, "DD-MM-YYYY", true);
    if (!expiry.isValid()) return false;

    return expiry.isSameOrAfter(today, "day");
  };

  // Get coupons
  const getCoupons = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getAllCoupons(reqData);
    if (response?.status === 200 && response?.data) {
      const couponsData = Array.isArray(response?.data) ? response.data : [];
      setCoupons(couponsData.filter(isCouponValidToday));
    }
  };

  // Get text rate and store all tax rates
  const getTextRate = async () => {
    const response = await appApiHandler.getGeneralSetting({
      user_id: userData?._id,
    });
    if (response?.status === 200 && response?.data) {
      setCod(response?.data?.cod);
      setOnline(response?.data?.online);
      // Store all tax rates in state
      setAllTaxRates(response?.data?.tax_rates || []);

      // Set tax rate based on selected address country
      if (response?.data?.tax_rates && selectedAddress?.country_id) {
        const countryTaxRate = response?.data?.tax_rates?.find(
          (taxRate) => taxRate?.country_id == selectedAddress?.country_id
        );

        if (countryTaxRate) {
          if (userData?.user_type === "wholeseller") {
            setTaxRate(countryTaxRate?.wholesale_tax_rate || 0);
          } else {
            setTaxRate(countryTaxRate?.retail_tax_rate || 0);
          }
        } else {
          setTaxRate(0);
        }
      }
    }
  };

  // Function to get tax rate by country ID from stored tax rates
  const getTaxRateByCountryId = (countryId) => {
    if (!countryId || allTaxRates.length === 0) return 0;

    const countryTaxRate = allTaxRates.find(
      (taxRate) => taxRate?.country_id == countryId
    );

    if (countryTaxRate) {
      if (userData?.user_type === "wholeseller") {
        return countryTaxRate?.wholesale_tax_rate || 0;
      } else {
        return countryTaxRate?.retail_tax_rate || 0;
      }
    }
    return 0;
  };

  // Get country list
  const getCountryList = async () => {
    const response = await appApiHandler.getCountries();
    if (response?.status === 200 && response?.data) {
      setCountryList(response?.data);
    }
  };

  useEffect(() => {
    if (isOpen && isLoggedIn && userData?._id) {
      setLoading(true);

      Promise.all([
        getAddresses(),
        getCoupons(),
        getTextRate(),
        getCountryList()
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [isOpen, isLoggedIn, userData?._id]);

  useEffect(() => {
    const calculateTotal = async () => {
      const price = await getPrice(selectedSize);
      setProductPrice(price);
      const subtotal = price * quantity;
      setSubTotal(subtotal);

      // Calculate tax amount
      const taxableAmount = (subtotal * taxRate) / 100;
      setTaxAmount(taxableAmount);
    };
    calculateTotal();
  }, [quantity, selectedSize, selectedCoupon, taxRate]);

  // Update tax rate when selected address changes
  useEffect(() => {
    if (selectedAddress?.country_id && allTaxRates.length > 0) {
      const newTaxRate = getTaxRateByCountryId(selectedAddress.country_id);
      setTaxRate(newTaxRate);
    }
  }, [selectedAddress, allTaxRates, userData?.user_type]);

  const handleIncreaseQty = () => {
    const newQty = quantity + 1;
    setQuantity(newQty);
    setInputQuantity(newQty.toString());
  };

  const handleDecreaseQty = () => {
    if (quantity > 1) {
      const newQty = quantity - 1;
      setQuantity(newQty);
      setInputQuantity(newQty.toString());
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputQuantity(value);
    }
  };

  const handleQuantityBlur = () => {
    const value = parseInt(inputQuantity);
    if (isNaN(value) || value < 1) {
      setInputQuantity("1");
      setQuantity(1);
    } else if (value > 999) {
      setInputQuantity("999");
      setQuantity(999);
    } else {
      setInputQuantity(value.toString());
      setQuantity(value);
    }
  };

  const handleQuantityKeyPress = (e) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const handleCouponSelect = async (coupon) => {
    try {
      // If coupon is already selected, remove it
      if (selectedCoupon?._id === coupon?._id) {
        setSelectedCoupon(null);
        return;
      }

      // Check minimum order amount
      if (coupon?.min_order_amount && parseFloat(subTotal) < parseFloat(coupon?.min_order_amount)) {
        const diffAmount = (coupon.min_order_amount - subTotal).toFixed(2);
        showErrorToast(`Add items worth ₹${diffAmount} more to apply this coupon`);
        return;
      }

      setApplyingCouponId(coupon?._id);
      const response = await appApiHandler.checkValidityOfCoupon({
        user_id: userData?._id,
        coupon_id: coupon?._id
      });

      if (response?.status === 200) {
        setSelectedCoupon(coupon);
      } else {
        showErrorToast(getErrorMessage(response?.message));
      }
    } catch (error) {
      showErrorToast(getErrorMessage());
    } finally {
      setApplyingCouponId(null);
    }
  };

  const handleOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    const reqData = {
      user_id: userData?._id,
      address_id: selectedAddress?._id,
      total: remainingAmount?.toFixed(2),
      tax_rate: taxRate,
      coupon_id: selectedCoupon?._id,
      product_id: product?._id,
      product_size: selectedSize?._id,
      product_qty: quantity,
      product_color: selectedColor?._id
    };
    setLoading(true);
    handleBuyNow(reqData);
  };

  const onConfirmationCancel = () => {
    setShowOrderConfirmation(false);
  };

  const handlePrevCoupon = () => {
    setCurrentCouponIndex((prev) => (prev === 0 ? coupons.length - 1 : prev - 1));
  };

  const handleNextCoupon = () => {
    setCurrentCouponIndex((prev) => (prev === coupons.length - 1 ? 0 : prev + 1));
  };

  const getDiscountText = (coupon) => {
    if (coupon.discount_type === "flat") {
      return `Flat ₹${coupon.discount_amount} off`;
    } else {
      return `${coupon.discount_percentage}% off`;
    }
  };

  const handleAddAddress = () => {
    setShowAddAddressForm(true);
  };

  const handleCancelAddressForm = () => {
    setShowAddAddressForm(false);
    resetAddressForm();
  };

  const resetAddressForm = () => {
    setName("");
    setPhoneno("");
    setCountryCode("+91");
    setCountry(null);
    setCountryModalVisible(false);
    setAddress("");
    setCity("");
    setState("");
    setPincode("");
    setButtonLoading(false);
    setNameError("");
    setPhonenoError("");
    setCountryError("");
    setAddressError("");
    setCityError("");
    setStateError("");
    setPincodeError("");
  };

  const validateAddressForm = () => {
    let isValid = true;

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter phone number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (!country) {
      setCountryError("Please select country");
      isValid = false;
    } else {
      setCountryError("");
    }

    if (!address.trim()) {
      setAddressError("Please enter address");
      isValid = false;
    } else {
      setAddressError("");
    }

    if (!city.trim()) {
      setCityError("Please enter city");
      isValid = false;
    } else {
      setCityError("");
    }

    if (!state.trim()) {
      setStateError("Please enter state");
      isValid = false;
    } else {
      setStateError("");
    }

    if (!pincode.trim()) {
      setPincodeError("Please enter pincode");
      isValid = false;
    } else {
      setPincodeError("");
    }

    return isValid;
  };

  const onSelectCountry = (selectedCountry) => {
    setCountry(selectedCountry);
    setCountryModalVisible(false);
  };

  const onPressCountry = () => {
    setCountryModalVisible(true);
  };

  const onCloseCountryModal = () => {
    setCountryModalVisible(false);
  };

  const onSubmitAddress = async () => {
    if (!validateAddressForm()) return;

    const reqData = {
      user_id: userData?._id,
      name: name.trim(),
      contact: phoneno.trim(),
      country_code: countryCode,
      address: address.trim(),
      country_id: country?._id,
      pincode: pincode.trim(),
      state: state.trim(),
      city: city.trim(),
    };

    setButtonLoading(true);
    try {
      const response = await appApiHandler.addAddress(reqData);
      if (response?.status === 200) {
        showSuccessToast("Address added successfully!");
        setShowAddAddressForm(false);
        resetAddressForm();
        // Refresh addresses list
        await getAddresses();
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (error) {
      console.log("Add Address Error", error);
      showErrorToast(string.error.failed_due_to_internal_error);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleProceed = () => {
    if (!selectedAddress) {
      showErrorToast("Please select a delivery address");
      return;
    }

    if (remainingAmount === 0) {
      const reqData = {
        user_id: userData?._id,
        address_id: selectedAddress?._id,
        paid: 1,
        total: remainingAmount?.toFixed(2),
        tax_rate: taxRate,
        coupon_id: selectedCoupon?._id,
        product_id: product?._id,
        product_size: selectedSize?._id,
        product_qty: quantity,
        product_color: selectedColor?._id
      };
      setLoading(true);
      handleBuyNow(reqData);
      return;
    }

    if (userData?.user_type === "wholeseller") {
      setShowOrderConfirmation(true);
    } else {
      setPaymentModalVisible(true);
    }
  };

  const handlePayment = async (method) => {
    setPaymentModalVisible(false);

    if (method === 0) {
      const reqData = {
        user_id: userData?._id,
        address_id: selectedAddress?._id,
        payment_method: "COD",
        paid: 0,
        total: remainingAmount?.toFixed(2),
        tax_rate: taxRate,
        coupon_id: selectedCoupon?._id,
        product_id: product?._id,
        product_size: selectedSize?._id,
        product_qty: quantity,
        product_color: selectedColor?._id
      };
      setLoading(true);
      handleBuyNow(reqData);
    } else {
      setLoading(true);
      const orderData = {
        amount: remainingAmount * 100,
        currency: "INR",
        user_id: userData?._id,
      };
      const response = await appApiHandler.createOrder(orderData);
      if (response?.status === 200 && response?.order_id) {
        proceedToCheckout(response?.order_id);
      } else if (response?.message) {
        setLoading(false);
        showErrorToast(response?.message);
      } else {
        setLoading(false);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const proceedToCheckout = async (orderId) => {
    setLoading(true);

    try {
      const config = getRazorpayConfig();

      const response = await initializeRazorpayCheckout({
        ...config,
        amount: remainingAmount * 100,
        order_id: orderId,
        prefill: {
          contact: selectedAddress?.phoneno,
          email: userData?.email,
          name: userData?.name,
        },
      })
        .then((data) => {
          if (data?.razorpay_payment_id) {
            const reqData = {
              user_id: userData?._id,
              address_id: selectedAddress?._id,
              payment_method: "Online",
              paid: 1,
              total: remainingAmount?.toFixed(2),
              tax_rate: taxRate,
              coupon_id: selectedCoupon?._id,
              product_id: product?._id,
              product_size: selectedSize?._id,
              product_qty: quantity,
              product_color: selectedColor?._id,
              payment_id: data?.razorpay_payment_id,
            };
            setLoading(true);
            handleBuyNow(reqData);
          } else {
            setLoading(false);
            showErrorToast("Payment failed. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Razorpay payment gateway error:", error);
          setLoading(false);

          if (
            error?.message?.includes("cancelled") ||
            error?.message?.includes("canceled")
          ) {
            showErrorToast("Payment was cancelled");
          } else if (error?.message?.includes("failed")) {
            showErrorToast("Payment failed. Please try again.");
          } else if (
            error?.message?.includes("network") ||
            error?.message?.includes("connection")
          ) {
            showErrorToast(
              "Network error. Please check your connection and try again."
            );
          } else {
            showErrorToast("Payment failed. Please try again.");
          }
        });
    } catch (error) {
      console.error("Razorpay payment error:", error);
      setLoading(false);
      if (error?.code === 0) {
        showErrorToast("Payment cancelled");
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const handleBuyNow = async (reqData) => {
    try {
      const response = await appApiHandler.buyNow(reqData);
      if (response?.status === 200) {
        showSuccessToast("Order placed successfully!");
        onClose();
        navigate("/orders");
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (error) {
      console.log("Buy Now Error", error);
      showErrorToast(string.error.failed_due_to_internal_error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setQuantity(1);
    setInputQuantity("1");
    setLoading(false);
    setAddress([]);
    setSelectedAddress(null);
    setCoupons([]);
    setSelectedCoupon(null);
    setCurrentCouponIndex(0);
    setApplyingCouponId(null);
    setOfferModalVisible(false);
    setSubTotal(0);
    setProductPrice(0);
    setTaxRate(0);
    setTaxAmount(0);
    setCod(true);
    setOnline(true);
    setAllTaxRates([]);
    setShowAddAddressForm(false);
    resetAddressForm();
    setPaymentModalVisible(false);
    setShowOrderConfirmation(false);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="buyNowOverlay" onClick={handleModalClose}>
      <div className="buyNowDrawer" onClick={(e) => e.stopPropagation()}>
        <div className="buyNowHeader">
          <h3>BUY NOW</h3>
          <button className="buyNowClose" onClick={handleModalClose}>
            <IoClose size={23} color={colors.primary} />
          </button>
        </div>

        <div className="buyNowContent">
          {/* Product Details */}
          <div className="buyNow-product-drawer">
            <div className="buyNow-product-drawer-row">
              <img
                src={getOptimizedUrl(product?.images?.[0]?.image)}
                alt={product?.name}
                className="buyNow-product-drawer-image"
                loading="lazy"
              />
              <div className="buyNow-product-drawer-details">
                <h3 className="buyNow-product-drawer-name" title={product?.name}>
                  {product?.name}
                </h3>
                <p className="buyNow-product-drawer-detail">
                  {[selectedColor?.name, selectedSize?.name, selectedSize?.size]
                    .filter(Boolean)
                    .join(" / ")}
                </p>
                <div className="buyNow-product-drawer-price-row">
                  <span className="buyNow-product-drawer-price">
                    {RUPEE}
                    {productPrice}
                  </span>
                </div>
                <div className="buyNow-product-drawer-controls">
                  <div className="buyNow-drawer-quantity-box">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecreaseQty();
                      }}
                      className="buyNow-drawer-qty-btn"
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputQuantity}
                      onChange={handleQuantityChange}
                      onBlur={handleQuantityBlur}
                      onKeyPress={handleQuantityKeyPress}
                      className="buyNow-drawer-qty-input"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleIncreaseQty();
                      }}
                      className="buyNow-drawer-qty-btn"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="buyNowSection">
            <h4 className="buyNowSectionTitle">Delivery Address</h4>
            {!showAddAddressForm ? (
              <>
                {addresses?.length > 0 ? (
                  <div className="buyNowAddressList">
                    {addresses?.map((address) => (
                      <div
                        key={address?._id}
                        className={`buyNowAddressItem ${selectedAddress?._id === address?._id ? 'selected' : ''}`}
                        onClick={() => setSelectedAddress(address)}
                      >
                        <div className="buyNowAddressText">
                          <p className="buyNowAddressName">{address?.name}</p>
                          <p className="buyNowAddressPhone">{address?.country_code}{address?.contact}</p>
                          <p className="buyNowAddressLine">{address?.address}</p>
                          <p className="buyNowAddressCity">
                            {address?.city}, {address?.state}, {address?.country?.name} - {address?.pincode}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="buyNowNoAddress">
                    <CommonButton
                      title="Add Address"
                      onPress={handleAddAddress}
                      customClassName="buyNowAddAddressBtn"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="buyNowAddAddressForm">
                <div className="buyNowFormTitle">ADD A NEW ADDRESS</div>

                {/* One field per row */}
                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <CommonInput
                      value={name}
                      onChange={setName}
                      error={nameError}
                      editable={!buttonLoading}
                      placeholder="Enter name"
                    />
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <PhoneInput
                      code={countryCode}
                      onChangeCode={(country) => setCountryCode(country.dial_code)}
                      onChangeText={setPhoneno}
                      value={phoneno}
                      placeholder="Enter phone number"
                      error={phonenoError}
                    />
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <CommonInput
                      value={address}
                      onChange={setAddress}
                      error={addressError}
                      editable={!buttonLoading}
                      placeholder="Enter address"
                      multiline={true}
                    />
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <div className="buyNowCountryContainer">
                      <button
                        className={`buyNowCountryInputField ${countryError ? "error" : ""}`}
                        onClick={onPressCountry}
                        type="button"
                      >
                        <span className={`buyNowCountryText ${country ? "selected" : ""}`}>
                          {country ? country?.name : "Select country"}
                        </span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 9L12 15L18 9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      {countryError && (
                        <div className="buyNowCountryError">{countryError}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <CommonInput
                      value={city}
                      onChange={setCity}
                      error={cityError}
                      placeholder="Enter city"
                      editable={!buttonLoading}
                    />
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <CommonInput
                      value={state}
                      onChange={setState}
                      error={stateError}
                      placeholder="Enter state"
                      editable={!buttonLoading}
                    />
                  </div>
                </div>

                <div className="buyNowFormRow">
                  <div className="buyNowFormField full-width">
                    <CommonInput
                      value={pincode}
                      onChange={setPincode}
                      error={pincodeError}
                      placeholder="Enter pincode"
                      editable={!buttonLoading}
                    />
                  </div>
                </div>

                <div className="buyNowFormActions">
                  <CommonButton
                    title={buttonLoading ? "Saving..." : "Save"}
                    onPress={onSubmitAddress}
                    disabled={buttonLoading}
                    customClassName="buyNowSaveAddressBtn"
                  />
                  <CommonButton
                    title="Cancel"
                    onPress={handleCancelAddressForm}
                    disabled={buttonLoading}
                    customClassName="buyNowCancelAddressBtn"
                    outline={true}
                    outlined
                  />
                </div>
              </div>
            )}
          </div>

          {/* Coupon Section */}
          {coupons?.length > 0 && (
            <div className="buyNowSection">
              <div className="buyNowCouponHeader">
                <h4 className="buyNowSectionTitle">Available Coupons</h4>
                <button
                  className="buyNowViewAllBtn"
                  onClick={() => setOfferModalVisible(true)}
                >
                  View All
                </button>
              </div>
              <div className="buyNowCouponCarousel">
                <button
                  className="coupon-nav-btn prev-btn"
                  onClick={handlePrevCoupon}
                  disabled={coupons.length <= 1}
                >
                  <FaChevronLeft />
                </button>

                <div className="coupon-carousel-container">
                  {coupons?.map((coupon, index) => (
                    <div
                      key={coupon?._id}
                      className={`coupon-card-wrapper ${index === currentCouponIndex ? 'active' : ''}`}
                      style={{ display: index === currentCouponIndex ? 'block' : 'none' }}
                    >
                      <div
                        className={`buyNowOfferCard`}
                      >
                        <div className="buyNowOfferHeader">
                          <div className="buyNowOfferCode">{coupon?.code}</div>
                          <div className="buyNowOfferDiscount">
                            {getDiscountText(coupon)}
                          </div>
                        </div>
                        {coupon?.description && (
                          <div className="buyNowOfferDescription">{coupon.description}</div>
                        )}
                        {coupon?.expiry_date && (
                          <div className="buyNowOfferValidity">
                            Valid until&nbsp;{coupon?.expiry_date}
                          </div>
                        )}
                        <button
                          className={`buyNowApplyButton ${selectedCoupon?._id === coupon?._id ? 'buyNowApplyButton-remove' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCouponSelect(coupon);
                          }}
                        >
                          {selectedCoupon?._id === coupon?._id ? 'Remove' : applyingCouponId === coupon?._id ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="coupon-nav-btn next-btn"
                  onClick={handleNextCoupon}
                  disabled={coupons.length <= 1}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {/* Order Summary */}
          {(taxAmount > 0 || selectedCoupon) && (
            <div className="buyNowSection">
              <h4 className="buyNowSectionTitle">Order Summary</h4>
              <div className="buyNowSummary">
                <div className="buyNowSummaryRow">
                  <span>Subtotal</span>
                  <span>{RUPEE} {subTotal?.toFixed(2)}</span>
                </div>
                {taxAmount > 0 && (
                  <div className="buyNowSummaryRow">
                    <span>Tax Rate ({taxRate}%):</span>
                    <span>{RUPEE} {taxAmount?.toFixed(2)}</span>
                  </div>
                )}
                {selectedCoupon && (
                  <div className="buyNowSummaryRow">
                    <span>Discount</span>
                    <span>-{RUPEE} {couponDiscount?.toFixed(2)}</span>
                  </div>
                )}
                {selectedCoupon && (
                  <div className="buyNowSummaryRow">
                    <span>Total</span>
                    <span>{RUPEE} {remainingAmount?.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="buyNowPayableAmountContainer">
            <span className="buyNowPayableAmount">Payable Amount</span>
            <span className="buyNowPayableAmountText">
              {RUPEE} {remainingAmount?.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="buyNowFooter">
          <CommonButton
            title={"Proceed"}
            onPress={handleProceed}
            disabled={!selectedAddress}
            customClassName="buyNowProceedBtn"
          />
        </div>

        {loading && (
          <div className="buyNowLoadingOverlay">
            <div className="buyNowLoadingSpinner"></div>
          </div>
        )}

        {/* Country Modal */}
        <CountryModal
          visible={countryModalVisible}
          onClose={onCloseCountryModal}
          onSelectCountry={onSelectCountry}
          selectedCountry={country}
          countryList={countryList}
        />

        {/* Offers Modal */}
        <OffersModal
          visible={offerModalVisible}
          onClose={() => setOfferModalVisible(false)}
          coupons={coupons}
          subTotal={subTotal}
          selectedCoupon={selectedCoupon}
          onApplyCoupon={setSelectedCoupon}
          onRemoveCoupon={() => setSelectedCoupon(null)}
        />

        {/* Payment Modal */}
        <PaymentModal
          visible={paymentModalVisible}
          onClose={() => setPaymentModalVisible(false)}
          onHandleSelect={handlePayment}
          cod={cod}
          online={online}
        />

        {/* Order Confirmation Modal */}
        <ConfirmationModal
          visible={showOrderConfirmation}
          title="Confirmation"
          description="Are you sure you want to place this order?"
          rightButtonText="Yes"
          leftButtonText="No"
          onRightButtonPress={handleOrderConfirmation}
          onLeftButtonPress={onConfirmationCancel}
        />
      </div>
    </div>
  );
};

export default BuyNowModal;